import { useEffect, useRef, useState } from 'react'
import { CalendarDays, CalendarRange, ChevronDown, Clock, Users } from 'lucide-react'

export type CalendarView = 'day' | 'week' | 'month' | 'teachers'

export const VIEWS: { value: CalendarView; label: string; Icon: typeof Clock }[] = [
  { value: 'day', label: 'Day', Icon: Clock },
  { value: 'week', label: 'Week', Icon: CalendarRange },
  { value: 'month', label: 'Month', Icon: CalendarDays },

  // The workbook's block 3: one lane per teacher, so a double-booking is
  // visible rather than something to be checked for.
  { value: 'teachers', label: 'By teacher', Icon: Users },
]

/**
 * Switches between day, week, and month.
 *
 * Sits with the date controls rather than in the site header: they are all the
 * same kind of thing (which slice of the calendar am I looking at), and keeping
 * them together leaves the header free for navigation that is genuinely global.
 *
 * Switching carries the current date, so moving from a day in October to the
 * week view lands on that week rather than today.
 *
 * **Segmented at three, a dropdown at four** (D-359). The children's half of
 * Schedule had a segmented toggle and the teaching half a dropdown, one tap
 * apart inside one destination, and neither knew about the other — the drift
 * that happens whenever one idea gets two implementations. The owner's question
 * was *"should those be aligned with one another?"*, and they are, by this
 * component rather than by two components agreeing.
 *
 * **The form follows the number of options, never the caller.** Three is worth
 * showing open: the current view is readable without a tap, and choosing is one
 * tap rather than two. Four does not fit a phone that way, and the fourth is
 * *By teacher*, which only the studio calendar offers (D-321). Keying on the
 * count means nobody has to remember which screen gets which — and the day a
 * fifth view appears, every caller changes together or none does.
 */
export default function CalendarViewSwitcher({
  current,
  onSelect,
  hideTeachers,
}: {
  current: CalendarView
  /**
   * What to do when a view is picked. **Required** (D-395).
   *
   * **It used to be optional, and falling through navigated to
   * `/calendar/{view}/{date}`** — four routes that all carry
   * `RequireCapability("ViewAllSchedules")`. So an embed whose caller forgot the
   * prop silently offered a parent or a teacher three controls landing on *"Not
   * your page"*, from one omission that is invisible at the call site. D-312 is
   * that bug having already happened once, and the fix then was to add the
   * escape hatch rather than to close the trap.
   *
   * **The type closes it.** With no default there is nothing to fall through
   * to, so a caller cannot forget — and this component no longer knows any
   * routes, which is the deeper point: it renders three buttons and reports
   * which was pressed. The screens that genuinely want to navigate are the ones
   * already behind that capability, and they now say so themselves.
   */
  onSelect: (view: CalendarView) => void
  /**
   * Drops "By teacher" from the list (D-321).
   *
   * **It is Tonya's view, not a teacher's.** One lane per teacher across a day,
   * with a conflict filter and a "find a teacher" box — it answers *"is anybody
   * double-booked?"*, which is a question about running a studio. A teacher's
   * own calendar is scoped to her (D-83), so it renders exactly one lane: hers.
   * That is a worse Day view.
   *
   * Owner-raised after asking what it was for, which was the better question —
   * it was wired up only because leaving it out rendered a blank panel.
   */
  hideTeachers?: boolean
}) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const offered = hideTeachers ? VIEWS.filter((v) => v.value !== 'teachers') : VIEWS
  const active = VIEWS.find((v) => v.value === current)!
  const ActiveIcon = active.Icon

  const go = (view: CalendarView) => {
    setOpen(false)
    onSelect(view)
  }

  /*
   * Open, because three fit. `aria-pressed` rather than `menuitemradio`: there
   * is no menu to be inside, and a toggle that lies about its role is read out
   * wrong by everything that cares.
   */
  if (offered.length <= 3) {
    return (
      <div className="flex gap-1 rounded-xl bg-surface-2 p-1">
        {offered.map(({ value, label, Icon }) => {
          const isActive = value === current
          return (
            <button
              key={value}
              type="button"
              onClick={() => go(value)}
              aria-pressed={isActive}
              /*
               * `py-1.5` on 12.5px text clears the 24px tap floor the layout
               * audit enforces (D-255). The icon is `hidden sm:inline` because
               * three labelled segments plus three icons is wider than a 390px
               * header has to give once *Book a room* is beside it.
               */
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${
                isActive ? 'bg-surface text-ink shadow-sm' : 'text-ink-3 hover:text-ink-2'
              }`}
            >
              <Icon size={14} strokeWidth={2} className="hidden shrink-0 sm:inline" />
              {label}
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13px] font-semibold text-ink-2 transition-colors hover:border-ink-3"
      >
        <ActiveIcon size={15} strokeWidth={2} />
        {active.label}
        <ChevronDown size={13} strokeWidth={2.5} className="text-ink-3" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 z-20 mt-1 w-36 overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-lg"
        >
          {offered.map(({ value, label, Icon }) => {
            const isActive = value === current
            return (
              <button
                key={value}
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => go(value)}
                className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-[12.5px] font-semibold transition-colors ${
                  isActive ? 'bg-accent-soft text-accent-2' : 'text-ink-2 hover:bg-surface-2'
                }`}
              >
                <Icon size={14} strokeWidth={2} className="shrink-0" />
                <span className="flex-1">{label}</span>
                {isActive && <span className="text-[11px] font-bold">&#10003;</span>}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
